import * as i0 from '@angular/core';
import { Directive, HostBinding, Component, ChangeDetectionStrategy, Input, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

/**
 * Directive that is used to wrap content that serves as a prefix in the ps-header.
 */
class PsHeaderPrefixDirective {
    get psHeaderPrefix() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderPrefixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsHeaderPrefixDirective, selector: "[psHeaderPrefix],ps-header-prefix", host: { properties: { "class.ps-header-prefix": "this.psHeaderPrefix" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderPrefixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psHeaderPrefix],ps-header-prefix'
                }]
        }], propDecorators: { psHeaderPrefix: [{
                type: HostBinding,
                args: ['class.ps-header-prefix']
            }] } });
/**
 * Directive that is used to wrap content that serves as a middle content in the ps-header.
 */
class PsHeaderContentDirective {
    get psHeaderContent() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsHeaderContentDirective, selector: "[psHeaderContent],ps-header-content", host: { properties: { "class.ps-header-content": "this.psHeaderContent" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psHeaderContent],ps-header-content'
                }]
        }], propDecorators: { psHeaderContent: [{
                type: HostBinding,
                args: ['class.ps-header-content']
            }] } });
/**
 * Directive that is used to wrap content that serves as a suffix in the ps-header.
 */
class PsHeaderSuffixDirective {
    get psHeaderSuffix() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderSuffixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsHeaderSuffixDirective, selector: "[psHeaderSuffix],ps-header-suffix", host: { properties: { "class.ps-header-suffix": "this.psHeaderSuffix" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderSuffixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psHeaderSuffix],ps-header-suffix'
                }]
        }], propDecorators: { psHeaderSuffix: [{
                type: HostBinding,
                args: ['class.ps-header-suffix']
            }] } });

var PsHeaderDisplayDensity;
(function (PsHeaderDisplayDensity) {
    PsHeaderDisplayDensity["Regular"] = "regular";
    PsHeaderDisplayDensity["Compact"] = "compact";
})(PsHeaderDisplayDensity || (PsHeaderDisplayDensity = {}));

let nextUniqueId = 0;
class PsHeaderComponent {
    constructor() {
        this._uid = `ps-header-${(nextUniqueId += 1)}`;
        this._hideShadow = false;
        this._displayDensity = PsHeaderDisplayDensity.Regular;
        /** A unique id for the header. If none is supplied, it will be auto-generated. */
        this.id = this._uid;
        /** Used to set the aria-label attribute on the header. */
        this.ariaLabel = '';
        /** Used to set the aria-labelledby attribute on the header. */
        this.ariaLabelledby = '';
    }
    /** The role attribute of the header. */
    get role() {
        return 'navigation';
    }
    /** Whether the shadow of the header is hidden or not. */
    get hideShadow() {
        return this._hideShadow;
    }
    set hideShadow(value) {
        this._hideShadow = coerceBooleanProperty(value);
    }
    /** Used to specify the header display density. */
    get displayDensity() {
        return this._displayDensity;
    }
    set displayDensity(value) {
        if (this._displayDensity !== value) {
            this._displayDensity = value;
        }
    }
    get psHeader() {
        return true;
    }
    get psHeaderCompact() {
        return this.displayDensity === PsHeaderDisplayDensity.Compact;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: PsHeaderComponent, selector: "ps-header", inputs: { id: "id", ariaLabel: ["aria-label", "ariaLabel"], ariaLabelledby: ["aria-labelledby", "ariaLabelledby"], hideShadow: "hideShadow", displayDensity: "displayDensity" }, host: { properties: { "attr.id": "this.id", "attr.aria-label": "this.ariaLabel", "attr.aria-labelledby": "this.ariaLabelledby", "attr.role": "this.role", "class.ps-header-shadowless": "this.hideShadow", "class.ps-header": "this.psHeader", "class.ps-header-compact": "this.psHeaderCompact" } }, ngImport: i0, template: "<header class=\"ps-header-wrapper\">\n  <div class=\"ps-header-prefix-wrapper\">\n    <ng-content select=\"[psHeaderPrefix],ps-header-prefix\"></ng-content>\n  </div>\n  <div class=\"ps-header-content-wrapper\">\n    <ng-content select=\"[psHeaderContent],ps-header-content\"></ng-content>\n  </div>\n  <div class=\"ps-header-suffix-wrapper\">\n    <ng-content select=\"[psHeaderSuffix],ps-header-suffix\"></ng-content>\n  </div>\n</header>\n", styles: [":host{display:flex;justify-content:center;position:relative;width:100%;z-index:2;min-width:23rem;height:4rem;padding:.5rem 1.5rem;overflow:hidden;-webkit-user-select:none;user-select:none}:host .ps-header-wrapper{width:100%;min-width:20rem;display:flex;flex-wrap:nowrap}:host .ps-header-wrapper .ps-header-prefix-wrapper{display:flex;align-items:center;margin-right:2.5rem}:host .ps-header-wrapper .ps-header-content-wrapper{display:flex;align-items:center;justify-content:center;flex-grow:1}:host .ps-header-wrapper .ps-header-suffix-wrapper{display:flex;align-items:center;margin-left:2.5rem}:host.ps-header-compact .ps-header-wrapper{max-width:93.75rem}@media all and (max-width: 600px){:host{height:3.5rem;min-width:22rem;padding:.5rem 1rem}:host .ps-header-wrapper .ps-header-prefix-wrapper{margin-right:.5rem}:host .ps-header-wrapper .ps-header-suffix-wrapper{margin-left:.5rem}}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-header', changeDetection: ChangeDetectionStrategy.OnPush, template: "<header class=\"ps-header-wrapper\">\n  <div class=\"ps-header-prefix-wrapper\">\n    <ng-content select=\"[psHeaderPrefix],ps-header-prefix\"></ng-content>\n  </div>\n  <div class=\"ps-header-content-wrapper\">\n    <ng-content select=\"[psHeaderContent],ps-header-content\"></ng-content>\n  </div>\n  <div class=\"ps-header-suffix-wrapper\">\n    <ng-content select=\"[psHeaderSuffix],ps-header-suffix\"></ng-content>\n  </div>\n</header>\n", styles: [":host{display:flex;justify-content:center;position:relative;width:100%;z-index:2;min-width:23rem;height:4rem;padding:.5rem 1.5rem;overflow:hidden;-webkit-user-select:none;user-select:none}:host .ps-header-wrapper{width:100%;min-width:20rem;display:flex;flex-wrap:nowrap}:host .ps-header-wrapper .ps-header-prefix-wrapper{display:flex;align-items:center;margin-right:2.5rem}:host .ps-header-wrapper .ps-header-content-wrapper{display:flex;align-items:center;justify-content:center;flex-grow:1}:host .ps-header-wrapper .ps-header-suffix-wrapper{display:flex;align-items:center;margin-left:2.5rem}:host.ps-header-compact .ps-header-wrapper{max-width:93.75rem}@media all and (max-width: 600px){:host{height:3.5rem;min-width:22rem;padding:.5rem 1rem}:host .ps-header-wrapper .ps-header-prefix-wrapper{margin-right:.5rem}:host .ps-header-wrapper .ps-header-suffix-wrapper{margin-left:.5rem}}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.id']
            }], ariaLabel: [{
                type: Input,
                args: ['aria-label']
            }, {
                type: HostBinding,
                args: ['attr.aria-label']
            }], ariaLabelledby: [{
                type: Input,
                args: ['aria-labelledby']
            }, {
                type: HostBinding,
                args: ['attr.aria-labelledby']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], hideShadow: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.ps-header-shadowless']
            }], displayDensity: [{
                type: Input
            }], psHeader: [{
                type: HostBinding,
                args: ['class.ps-header']
            }], psHeaderCompact: [{
                type: HostBinding,
                args: ['class.ps-header-compact']
            }] } });

class PsHeaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderModule, declarations: [PsHeaderComponent,
            PsHeaderPrefixDirective,
            PsHeaderContentDirective,
            PsHeaderSuffixDirective], imports: [CommonModule], exports: [PsHeaderComponent,
            PsHeaderPrefixDirective,
            PsHeaderContentDirective,
            PsHeaderSuffixDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PsHeaderComponent,
                        PsHeaderPrefixDirective,
                        PsHeaderContentDirective,
                        PsHeaderSuffixDirective
                    ],
                    imports: [CommonModule],
                    exports: [
                        PsHeaderComponent,
                        PsHeaderPrefixDirective,
                        PsHeaderContentDirective,
                        PsHeaderSuffixDirective
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PsHeaderComponent, PsHeaderContentDirective, PsHeaderDisplayDensity, PsHeaderModule, PsHeaderPrefixDirective, PsHeaderSuffixDirective };
